import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Plus, Trash2, Search, ArrowUpDown } from "lucide-react";
import { statusLabel, statusColor, fmtEur, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import AttachmentsField from "@/components/AttachmentsField";

const empty = { client_id: "", name: "", description: "", status: "in_corso", progress: 0,
  hourly_rate: 0, hours_estimated: 0, fixed_price: 0, costs: 0, deadline: "", attachments: [] };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/projects"), api.get("/clients")]);
    setProjects(p.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const clientLabel = (id) => {
    const c = clients.find((x) => x.client_id === id);
    if (!c) return "—";
    return c.company || c.name || "—";
  };
  // Sort clients alphabetically by company for the dropdowns (both form + filter)
  const clientsForSelect = useMemo(
    () => [...clients].sort((a, b) => (a.company || a.name || "").localeCompare(b.company || b.name || "", "it", { sensitivity: "base" })),
    [clients],
  );
  // Only clients that actually have at least one project — used by the filter dropdown.
  const clientsWithProjects = useMemo(() => {
    const ids = new Set(projects.map((p) => p.client_id));
    return clientsForSelect.filter((c) => ids.has(c.client_id));
  }, [clientsForSelect, projects]);

  const visibleProjects = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (clientFilter !== "all" && p.client_id !== clientFilter) return false;
      if (!term) return true;
      const hay = `${p.name || ""} ${p.description || ""} ${clientLabel(p.client_id)}`.toLowerCase();
      return hay.includes(term);
    });
    const ts = (p) => new Date(p.updated_at || p.created_at || 0).getTime();
    const cmp = {
      updated_desc: (a, b) => ts(b) - ts(a),
      updated_asc: (a, b) => ts(a) - ts(b),
      alpha_asc: (a, b) => (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" }),
    }[sortBy] || (() => 0);
    return [...list].sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, statusFilter, clientFilter, q, sortBy, clients]);

  const save = async () => {
    if (!form.client_id) return toast.error("Seleziona un cliente");
    try {
      if (editingId) await api.put(`/projects/${editingId}`, form);
      else await api.post("/projects", form);
      toast.success(editingId ? "Progetto aggiornato" : "Progetto creato");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il progetto?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Eliminato"); load();
  };

  const openNew = () => { setForm({...empty, client_id: clients[0]?.client_id || ""}); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({...empty, ...p}); setEditingId(p.project_id); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Progetti</span>
          <h1 className="mt-2 font-heading text-3xl">I tuoi progetti</h1>
          <p className="text-zen-ink2 mt-1">{projects.length} progetti totali · {projects.filter(p=>p.status==="in_corso").length} in corso</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-ink2"/>
            <Input
              data-testid="search-projects"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Cerca…"
              className="pl-9 w-56"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-project-status" className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="in_corso">In corso</SelectItem>
              <SelectItem value="in_pausa">In pausa</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
              <SelectItem value="annullato">Chiuso</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="filter-project-client" className="w-48"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {clientsWithProjects.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>
                  {c.company || c.name || "—"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-projects" className="w-56">
              <ArrowUpDown size={14} className="text-zen-ink2 mr-1"/>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_desc">Più recenti</SelectItem>
              <SelectItem value="updated_asc">Meno recenti</SelectItem>
              <SelectItem value="alpha_asc">Nome A → Z</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-project-btn" className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
              <Plus size={16} className="mr-1"/> Nuovo progetto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Modifica progetto" : "Nuovo progetto"}</DialogTitle>
              <DialogDescription className="sr-only">Compila i dettagli del progetto: cliente, avanzamento, tariffe e allegati.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div><Label>Nome *</Label><Input data-testid="pf-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/></div>
              <div>
                <Label>Cliente (azienda) *</Label>
                <Select value={form.client_id} onValueChange={(v)=>setForm({...form, client_id:v})}>
                  <SelectTrigger data-testid="pf-client"><SelectValue placeholder="Seleziona un'azienda…"/></SelectTrigger>
                  <SelectContent>{clientsForSelect.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>
                      {c.company || c.name || "—"}
                    </SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
              <div><Label>Descrizione</Label><Textarea rows={2} value={form.description||""} onChange={(e)=>setForm({...form, description:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Stato</Label>
                  <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                    <SelectTrigger data-testid="pf-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_corso">In corso</SelectItem>
                      <SelectItem value="in_pausa">In pausa</SelectItem>
                      <SelectItem value="completato">Completato</SelectItem>
                      <SelectItem value="annullato">Chiuso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Scadenza</Label><Input type="date" value={form.deadline?.slice(0,10)||""} onChange={(e)=>setForm({...form, deadline:e.target.value})}/></div>
              </div>
              <div>
                <Label>Avanzamento: {form.progress}%</Label>
                <Slider data-testid="pf-progress" value={[form.progress]} max={100} step={5} onValueChange={([v])=>setForm({...form, progress:v})} className="mt-3"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tariffa oraria (€)</Label><Input type="number" step="0.5" value={form.hourly_rate} onChange={(e)=>setForm({...form, hourly_rate:parseFloat(e.target.value)||0})}/></div>
                <div><Label>Prezzo fisso (€)</Label><Input type="number" step="0.5" value={form.fixed_price} onChange={(e)=>setForm({...form, fixed_price:parseFloat(e.target.value)||0})}/></div>
                <div><Label>Ore stimate</Label><Input type="number" step="0.5" value={form.hours_estimated} onChange={(e)=>setForm({...form, hours_estimated:parseFloat(e.target.value)||0})}/></div>
                <div><Label>Costi esterni (€)</Label><Input type="number" step="0.5" value={form.costs} onChange={(e)=>setForm({...form, costs:parseFloat(e.target.value)||0})}/></div>
              </div>
              {editingId && (
                <div><Label>Ore registrate</Label><Input type="number" step="0.5" value={form.hours_logged||0} onChange={(e)=>setForm({...form, hours_logged:parseFloat(e.target.value)||0})}/></div>
              )}
              <div className="min-w-0">
                <Label>Allegati Google Drive</Label>
                <div className="mt-1.5">
                  <AttachmentsField
                    testId="pf-attachments"
                    value={form.attachments || []}
                    onChange={(next)=>setForm({...form, attachments: next})}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setOpen(false)}>Annulla</Button>
              <Button data-testid="pf-save" onClick={save} className="bg-zen-primary hover:bg-zen-primaryHover">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {visibleProjects.map((p) => {
          const rev = (p.revenue != null)
            ? p.revenue
            : ((p.fixed_price || 0) + (p.hours_logged || 0) * (p.hourly_rate || 0));
          const marg = rev > 0 ? Math.round((rev - (p.costs||0)) / rev * 100) : 0;
          return (
            <div key={p.project_id} data-testid={`project-card-${p.project_id}`} className="zen-card p-6 group">
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0">
                  <button onClick={()=>openEdit(p)} className="font-heading text-xl text-zen-ink hover:text-zen-primary text-left block truncate max-w-full">{p.name}</button>
                  <div className="text-sm text-zen-ink2 mt-0.5" data-testid={`project-client-${p.project_id}`}>{clientLabel(p.client_id)}</div>
                </div>
                <Badge className={`${statusColor[p.status]} rounded-full font-normal`}>{statusLabel[p.status]}</Badge>
              </div>
              {p.description && <p className="text-sm text-zen-ink2 mb-4 line-clamp-2">{p.description}</p>}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-zen-ink2 mb-1.5"><span>Avanzamento</span><span>{p.progress}%</span></div>
                <Progress value={p.progress} className="h-2"/>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zen-bg rounded-xl p-3"><div className="text-xs text-zen-ink2">Ricavi</div><div className="font-heading text-lg">{fmtEur(rev)}</div></div>
                <div className="bg-zen-bg rounded-xl p-3"><div className="text-xs text-zen-ink2">Ore</div><div className="font-heading text-lg">{p.hours_logged||0}/{p.hours_estimated||0}</div></div>
                <div className="bg-zen-bg rounded-xl p-3"><div className="text-xs text-zen-ink2">Margine</div><div className="font-heading text-lg">{marg}%</div></div>
              </div>
              <div className="mt-4 flex justify-between items-center text-sm text-zen-ink2">
                <span>Scadenza: {fmtDate(p.deadline)}</span>
                <button data-testid={`del-project-${p.project_id}`} onClick={()=>remove(p.project_id)} className="text-zen-alert hover:opacity-70 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={15}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {projects.length === 0 && (
        <div className="zen-card p-12 text-center text-zen-ink2">Nessun progetto ancora. Creane uno!</div>
      )}
      {projects.length > 0 && visibleProjects.length === 0 && (
        <div className="zen-card p-12 text-center text-zen-ink2">Nessun progetto corrisponde ai filtri attuali.</div>
      )}
    </div>
  );
}
